import { create } from 'zustand';

import type { GeoComplyTroubleshooterMessage } from './geoComplyTroubleshooter';

export type GeoComplyStatus = 'idle' | 'checking' | 'verified' | 'blocked' | 'verification-required';
export type GeoComplyCheckMode = 'passive' | 'step-up';

/**
 * Why a geolocation compliance check was denied, mirroring the mobile apps'
 * wire names. The `kalshi_*` verdict rules are configured on GeoComply's
 * side; the rest are rollups derived from the engine's error summary.
 */
export type GeoComplyDenyReason =
    | 'failed_with_precise_location'
    | 'not_tradeable_state'
    | 'approximate_location'
    | 'requires_precise_location'
    | 'unconfirmed_boundary'
    | 'out_of_boundary'
    | 'blocked_software'
    | 'unknown';

/**
 * Why another verification is required. This preserves both recoverable
 * backend classifications and failures that prevented the SDK from producing
 * a result so the UI can give the user an actionable next step.
 */
export type GeoComplyVerificationRequiredReason =
    | 'authorization_failed'
    | 'backend_error_code_missing'
    | 'backend_request_failed'
    | 'backend_response_invalid'
    | 'backend_user_mismatch'
    | 'check_failed'
    | 'check_timed_out'
    | 'denied_approximate_location'
    | 'denied_blocked_software_without_location_permission'
    | 'denied_boundary_exclusion'
    | 'denied_masking_service'
    | 'denied_not_tradeable_state_without_region'
    | 'denied_out_of_boundary_without_location_permission'
    | 'denied_requires_precise_location'
    | 'denied_unconfirmed_boundary'
    | 'denied_unknown_requires_precise_location'
    | 'denied_unknown_without_location_permission'
    | 'device_time_incorrect'
    | 'location_country_missing'
    | 'location_invalid_type'
    | 'location_missing'
    | 'location_us_region_missing'
    | 'no_coordinates'
    | 'sdk_no_result'
    | 'trust_window_elapsed_before_apply';

interface GeoComplyActions {
    sessionStarted: () => void;
    checkQueued: (mode: GeoComplyCheckMode) => void;
    checkStarted: (mode: GeoComplyCheckMode) => void;
    verificationPassed: (validUntil: number, usRegionCode?: string | null) => void;
    verificationFailed: (
        denyReason?: GeoComplyDenyReason,
        usRegionCode?: string | null,
        troubleshooterMessages?: readonly GeoComplyTroubleshooterMessage[],
    ) => void;
    checkInconclusive: (
        reason?: GeoComplyVerificationRequiredReason,
        errorCode?: number,
        troubleshooterMessages?: readonly GeoComplyTroubleshooterMessage[],
    ) => void;
    sessionEnded: () => void;
    sdkAvailabilityChanged: (isAvailable: boolean) => void;
}

interface GeoComplyState {
    status: GeoComplyStatus;
    checkMode: GeoComplyCheckMode | null;
    validUntil: number | null;
    /** Wall-clock time when GeoComply produced its latest pass/fail decision in this session. */
    terminalDecisionAt: number | null;
    /** US region code from the check's location; null outside the US or when unknown. */
    usRegionCode: string | null;
    /** Why the last check was denied; only meaningful while status is 'blocked'. */
    denyReason: GeoComplyDenyReason | null;
    /** Why another check is required; only meaningful while status is 'verification-required'. */
    verificationRequiredReason: GeoComplyVerificationRequiredReason | null;
    /** Privacy-safe SDK/backend diagnostic code associated with the required re-check. */
    verificationRequiredErrorCode: number | null;
    /** GeoComply-authored public recovery guidance from the latest verified result. */
    troubleshooterMessages: readonly GeoComplyTroubleshooterMessage[];
    /**
     * True when the BrowserGuard script failed to load or loaded without
     * exposing its SDK global (typically a content blocker), so no location
     * check can run until a reload. Tracks script availability rather than
     * session state, so session lifecycle actions leave it untouched.
     */
    sdkUnavailable: boolean;
    actions: GeoComplyActions;
}

export const useGeoComplyStore = create<GeoComplyState>()((set) => ({
    status: 'idle',
    checkMode: null,
    validUntil: null,
    terminalDecisionAt: null,
    usRegionCode: null,
    denyReason: null,
    verificationRequiredReason: null,
    verificationRequiredErrorCode: null,
    troubleshooterMessages: [],
    sdkUnavailable: false,
    actions: {
        sessionStarted: () =>
            set({
                status: 'checking',
                checkMode: 'passive',
                validUntil: null,
                terminalDecisionAt: null,
                usRegionCode: null,
                denyReason: null,
                verificationRequiredReason: null,
                verificationRequiredErrorCode: null,
                troubleshooterMessages: [],
            }),
        // Queuing gives unresolved checks immediate progress UI, but preserves
        // a still-current verification until the SDK transaction truly starts.
        checkQueued: (checkMode) =>
            set((state) => {
                if (isGeoComplyVerificationCurrent(state)) {
                    return state.checkMode === checkMode ? state : { checkMode };
                }
                if (state.status === 'checking' && state.checkMode === checkMode) return state;
                return {
                    status: 'checking',
                    checkMode,
                    validUntil: null,
                    usRegionCode: null,
                    denyReason: null,
                    verificationRequiredReason: null,
                    verificationRequiredErrorCode: null,
                    troubleshooterMessages: [],
                };
            }),
        // A passive refresh of a still-current verification runs in the
        // background: the previous result stays trusted (GeoComply's
        // buffer_time grace) so trading is not interrupted mid-check.
        checkStarted: (checkMode) =>
            set((state) => {
                if (state.status === 'checking' && state.checkMode === checkMode) return state;
                return checkMode === 'passive' && isGeoComplyVerificationCurrent(state)
                    ? { checkMode }
                    : {
                          status: 'checking',
                          checkMode,
                          validUntil: null,
                          usRegionCode: null,
                          denyReason: null,
                          verificationRequiredReason: null,
                          verificationRequiredErrorCode: null,
                          troubleshooterMessages: [],
                      };
            }),
        verificationPassed: (validUntil, usRegionCode = null) =>
            set({
                status: 'verified',
                checkMode: null,
                validUntil,
                terminalDecisionAt: Date.now(),
                usRegionCode,
                denyReason: null,
                verificationRequiredReason: null,
                verificationRequiredErrorCode: null,
                troubleshooterMessages: [],
            }),
        verificationFailed: (denyReason = 'unknown', usRegionCode = null, troubleshooterMessages = []) =>
            set({
                status: 'blocked',
                checkMode: null,
                validUntil: null,
                terminalDecisionAt: Date.now(),
                usRegionCode,
                denyReason,
                verificationRequiredReason: null,
                verificationRequiredErrorCode: null,
                troubleshooterMessages,
            }),
        // An inconclusive re-check keeps a still-current result trusted
        // through its buffered expiry; only a lapsed or absent verification
        // fails closed. Step-up checks reset to 'checking' first, so their
        // failures always land in the fail-closed branch.
        checkInconclusive: (reason, errorCode, troubleshooterMessages = []) =>
            set((state) =>
                isGeoComplyVerificationCurrent(state)
                    ? { checkMode: null }
                    : {
                          status: 'verification-required',
                          checkMode: null,
                          validUntil: null,
                          usRegionCode: null,
                          denyReason: null,
                          verificationRequiredReason: reason ?? null,
                          verificationRequiredErrorCode: errorCode ?? null,
                          troubleshooterMessages,
                      },
            ),
        sessionEnded: () =>
            set({
                status: 'idle',
                checkMode: null,
                validUntil: null,
                terminalDecisionAt: null,
                usRegionCode: null,
                denyReason: null,
                verificationRequiredReason: null,
                verificationRequiredErrorCode: null,
                troubleshooterMessages: [],
            }),
        sdkAvailabilityChanged: (isAvailable) => set({ sdkUnavailable: !isAvailable }),
    },
}));

export function getGeoComplyMonotonicTime(): number {
    return performance.now();
}

export function isGeoComplyVerificationCurrent(
    state: Pick<GeoComplyState, 'status' | 'validUntil'>,
    now = getGeoComplyMonotonicTime(),
): boolean {
    return state.status === 'verified' && state.validUntil != null && state.validUntil > now;
}

export function shouldShowGeoComplyIntervention(
    state: Pick<GeoComplyState, 'status' | 'checkMode' | 'validUntil'>,
    now = getGeoComplyMonotonicTime(),
): boolean {
    if (state.status === 'blocked' || state.status === 'verification-required') return true;
    if (state.status === 'checking') return state.checkMode === 'step-up';
    if (state.status === 'verified') return !isGeoComplyVerificationCurrent(state, now);

    return false;
}
